import { callGeotabAPI } from '../api/geotab-client';
import { splitDateRange, mapLimit, MAX_CONCURRENT, MIN_CHUNK_MS } from '../api/fetch-range';
import { calculateScores } from './safety-scoring';
import { renderSafetyResults } from './safety-results';
import type { Trip } from '../api/types';
import type { TaggedEvent, EntityData, RuleKey, ScoredEntity, SafetyAnalysisParams } from './safety-types';

// ── Module-level state consumed by safety-export ───────────────────────────

export let rawTaggedEvents: TaggedEvent[]           = [];
export let rawTrips:        Trip[]                  = [];
export let lastParams:      SafetyAnalysisParams | null = null;
export let scoredEntities:  ScoredEntity[]          = [];

export function updateSafetyAnalysisUnit(unit: 'km' | 'mi'): void {
  if (lastParams) lastParams = { ...lastParams, distUnit: unit };
}

// ── Constants ──────────────────────────────────────────────────────────────

const TRIP_BATCH       = 50;      // progress-label update interval (vehicles)
const EVENT_CHUNK_DAYS = 7;       // Date range split into 7-day windows per rule to start
const EVENT_PER_CHUNK  = 50000;   // Geotab per-call ceiling (no pagination) — truncates above this
const TRIP_PER_CALL    = 5000;    // Geotab per-call ceiling for Trips — same truncation risk
const UNASSIGNED_IDS   = new Set(['UnknownDriverId', 'NoDriver', 'b5']);

// ── Entry point ────────────────────────────────────────────────────────────

export async function runSafetyAnalysis(params: SafetyAnalysisParams): Promise<void> {
  lastParams      = params;
  rawTaggedEvents = [];
  rawTrips        = [];

  if (!params.vehicleIds.size) {
    showState('error', 'Select at least one vehicle before running.');
    return;
  }

  const enabled = params.ruleConfigs.filter(r => r.enabled);
  if (enabled.length < 4) {
    showState('error', 'Select at least 4 rules before running.');
    return;
  }

  try {
    // ── Phase 1: ExceptionEvents per rule in parallel ──────────────────────
    setLabel('Fetching exception events…');
    showState('loading');

    const dateChunks = splitDateRange(params.fromISO, params.toISO, EVENT_CHUNK_DAYS);
    let chunkLimitHit = false;

    // Fetch one (rule, window). If the window tops out at the cap, Geotab has truncated the
    // result, so split the window in half and recurse until every sub-window is under the cap.
    // This recovers all events automatically, regardless of fleet size or how busy a rule is —
    // no need to manually narrow the date range.
    const fetchWindow = async (
      ruleId: string, ruleKey: RuleKey, fromISO: string, toISO: string,
    ): Promise<TaggedEvent[]> => {
      const evs = await callGeotabAPI<any[]>('Get', {
        typeName: 'ExceptionEvent',
        search: { ruleSearch: { id: ruleId }, fromDate: fromISO, toDate: toISO },
        resultsLimit: EVENT_PER_CHUNK,
      }).catch(() => null);
      if (evs === null) return [];

      if (evs.length >= EVENT_PER_CHUNK) {
        const fromMs = new Date(fromISO).getTime();
        const toMs   = new Date(toISO).getTime();
        if (toMs - fromMs > MIN_CHUNK_MS) {
          const midISO = new Date(fromMs + Math.floor((toMs - fromMs) / 2)).toISOString();
          const [a, b] = await Promise.all([
            fetchWindow(ruleId, ruleKey, fromISO, midISO),
            fetchWindow(ruleId, ruleKey, midISO, toISO),
          ]);
          return a.concat(b);
        }
        chunkLimitHit = true; // even a 1-hour window is capped — genuinely extreme
      }
      return evs.map(e => ({ ...e, ruleKey } as TaggedEvent));
    };

    // Flatten (rule × window) into one task list and run with bounded concurrency.
    const eventTasks: Array<{ id: string; key: RuleKey; from: string; to: string }> = [];
    for (const rule of enabled)
      for (const [from, to] of dateChunks)
        eventTasks.push({ id: rule.id, key: rule.key, from, to });

    const ruleResults = await mapLimit(eventTasks, MAX_CONCURRENT,
      t => fetchWindow(t.id, t.key, t.from, t.to));

    // Dedup by event id — adjacent windows share a boundary timestamp, so an event sitting on
    // a boundary can be returned by both halves.
    const seen = new Set<string>();
    rawTaggedEvents = ruleResults.flat().filter(e => {
      const id = (e as { id?: string }).id;
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

    setWarn(chunkLimitHit
      ? `One rule logged over ${EVENT_PER_CHUNK.toLocaleString()} events in a single hour — results for that rule may be truncated.`
      : '');

    // ── Phase 2: Trips per device ─────────────────────────────────────────
    // Same adaptive split as events: a single device's trips are capped at 5,000 per call, which
    // a very high-trip vehicle can exceed over a long range (e.g. YTD). Splitting the window when
    // that happens keeps total distance from being silently undercounted (which would otherwise
    // inflate the event rate and unfairly lower that vehicle's score).
    const vehicleArr = [...params.vehicleIds];
    let tripsDone = 0;

    const fetchTrips = async (deviceId: string, fromISO: string, toISO: string): Promise<Trip[]> => {
      const trips = await callGeotabAPI<Trip[]>('Get', {
        typeName: 'Trip',
        search: { deviceSearch: { id: deviceId }, fromDate: fromISO, toDate: toISO },
        resultsLimit: TRIP_PER_CALL,
      }).catch(() => null);
      if (trips === null) return [];

      if (trips.length >= TRIP_PER_CALL) {
        const fromMs = new Date(fromISO).getTime();
        const toMs   = new Date(toISO).getTime();
        if (toMs - fromMs > MIN_CHUNK_MS) {
          const midISO = new Date(fromMs + Math.floor((toMs - fromMs) / 2)).toISOString();
          const [a, b] = await Promise.all([
            fetchTrips(deviceId, fromISO, midISO),
            fetchTrips(deviceId, midISO, toISO),
          ]);
          return a.concat(b);
        }
      }
      return trips;
    };

    const tripLists = await mapLimit(vehicleArr, MAX_CONCURRENT, async (id) => {
      const t = await fetchTrips(id, params.fromISO, params.toISO);
      tripsDone++;
      if (tripsDone % TRIP_BATCH === 0 || tripsDone === vehicleArr.length)
        setLabel(`Fetching trip distances (${tripsDone} of ${vehicleArr.length})…`);
      return t;
    });

    // Dedup by trip id — sub-split windows share a boundary timestamp.
    const seenTrips = new Set<string>();
    rawTrips = tripLists.flat().filter(t => {
      const id = (t as { id?: string }).id;
      if (!id) return true;
      if (seenTrips.has(id)) return false;
      seenTrips.add(id);
      return true;
    });

    // ── Phase 3: Aggregate, score, render ─────────────────────────────────
    setLabel('Calculating scores…');
    const rawData = aggregateEntityData(params);
    scoredEntities = calculateScores(rawData, params.ruleConfigs);
    renderSafetyResults(params, scoredEntities);

  } catch (err) {
    showState('error', 'Analysis failed: ' + (err as Error).message);
  }
}

// ── Aggregate raw events + trips into EntityData per entity ───────────────

function aggregateEntityData(params: SafetyAnalysisParams): Map<string, EntityData> {
  const { scoreBy, vehicleIds, driverIds, userMap, deviceMap, ruleConfigs } = params;
  const enabledKeys = new Set(ruleConfigs.filter(r => r.enabled).map(r => r.key));

  // Build empty byRule for all enabled keys
  const emptyByRule = (): EntityData['byRule'] => {
    const obj = {} as EntityData['byRule'];
    for (const key of enabledKeys) obj[key] = { eventCount: 0, exceptionDistKm: 0 };
    return obj;
  };

  const entityMap = new Map<string, EntityData>();

  const getOrCreate = (id: string, name: string): EntityData => {
    if (!entityMap.has(id)) {
      entityMap.set(id, { id, name, totalDistanceKm: 0, totalEvents: 0, byRule: emptyByRule() });
    }
    return entityMap.get(id)!;
  };

  // Pre-seed every selected vehicle so zero-activity ones still appear as
  // No Activity rows, matching Geotab's full-roster report (vehicle mode only).
  // Driver mode is left as-is because driver IDs can't be derived from vehicleIds.
  if (scoreBy === 'vehicle') {
    vehicleIds.forEach(id => {
      const name = deviceMap.get(id) ?? id;
      getOrCreate(id, name);
    });
  }

  // ── Sum trip distances ─────────────────────────────────────────────────
  for (const trip of rawTrips) {
    if (!vehicleIds.has(trip.device.id)) continue;
    const entityId = scoreBy === 'vehicle'
      ? trip.device.id
      : (trip.driver?.id ?? null);
    if (!entityId) continue;
    if (UNASSIGNED_IDS.has(entityId)) continue;
    if (scoreBy === 'driver' && driverIds.size > 0 && !driverIds.has(entityId)) continue;

    const name = scoreBy === 'vehicle'
      ? (deviceMap.get(trip.device.id) ?? trip.device.id)
      : (userMap.get(entityId) ?? entityId);

    getOrCreate(entityId, name).totalDistanceKm += trip.distance ?? 0;
  }

  // ── Aggregate exception events ─────────────────────────────────────────
  for (const ev of rawTaggedEvents) {
    if (!vehicleIds.has(ev.device.id)) continue;
    if (!enabledKeys.has(ev.ruleKey)) continue;

    const entityId = scoreBy === 'vehicle'
      ? ev.device.id
      : (ev.driver?.id ?? 'UnknownDriverId');

    if (UNASSIGNED_IDS.has(entityId)) continue;
    if (scoreBy === 'driver' && driverIds.size > 0 && !driverIds.has(entityId)) continue;

    const name = scoreBy === 'vehicle'
      ? (deviceMap.get(ev.device.id) ?? ev.device.id)
      : (userMap.get(entityId) ?? entityId);

    const entity = getOrCreate(entityId, name);
    entity.byRule[ev.ruleKey].eventCount      += 1;
    entity.byRule[ev.ruleKey].exceptionDistKm += ev.distance ?? 0;
    entity.totalEvents += 1;
  }

  return entityMap;
}

// ── DOM helpers ────────────────────────────────────────────────────────────

function showState(state: 'loading' | 'error', msg = ''): void {
  document.getElementById('safetyEmpty')  !.classList.add('hidden');
  document.getElementById('safetyResults')!.classList.add('hidden');

  const loading = document.getElementById('safetyLoading')!;
  const errBox  = document.getElementById('safetyErrBox')!;

  loading.classList.toggle('hidden', state !== 'loading');
  errBox .classList.toggle('hidden', state !== 'error');
  if (state === 'error') errBox.textContent = msg;
}

function setLabel(msg: string): void {
  const el = document.getElementById('safetyLoadingLabel');
  if (el) el.textContent = msg;
}

function setWarn(msg: string): void {
  const el = document.getElementById('safetyWarnBox');
  if (!el) return;
  el.textContent = msg;
  el.classList.toggle('hidden', !msg);
}
